import React, { useState } from "react";
import { AudioLines, Loader2, ScanLine, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, trackPreviewUrl } from "@/lib/api";
import { usePoll } from "@/hooks/usePoll";

const Bar = ({ t, best }) => {
  const pct = t.silent ? 0 : Math.max(4, Math.min(100, (t.score + 60) * 2));
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className={`w-16 font-mono ${best ? "text-[#E6B04E]" : ""}`}>{t.name}</span>
      <div className="flex-1 h-2 rounded-full bg-[#0c0e14] overflow-hidden"><div className={`h-full ${best ? "bg-[#E6B04E]" : "bg-[#3B4259]"}`} style={{ width: `${pct}%` }} /></div>
      <span className="w-40 font-mono text-[#94A3B8] text-right">{t.silent ? "silent" : `speech ${t.speech_db.toFixed(0)} dB · noise ${t.noise_floor_db.toFixed(0)} dB`}</span>
    </div>
  );
};

const Camera = ({ c, onPick }) => (
  <div className="studio-card p-4 space-y-3" data-testid={`tracks-camera-${c.id}`}>
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <div><div className="font-semibold text-sm">{c.label}</div><div className="metric-label" style={{ fontSize: 9 }}>{c.clips} clips · auto pick: Track {c.auto_track + 1}</div></div>
      <select className="studio-input !py-1 text-xs w-44" value={c.override ?? ""} data-testid={`tracks-select-${c.id}`}
        onChange={(e) => onPick(c.id, e.target.value === "" ? null : +e.target.value)}>
        <option value="">{`Auto (Track ${c.auto_track + 1})`}</option>
        {c.tracks.map((t) => <option key={t.index} value={t.index}>{`${t.name}${t.silent ? " (silent)" : ""}`}</option>)}
      </select>
    </div>
    {c.tracks.map((t) => (
      <div key={t.index} className="space-y-1">
        <Bar t={t} best={t.index === c.effective} />
        {!t.silent && c.sample_media_id && <audio controls preload="none" src={trackPreviewUrl(c.sample_media_id, t.index)} className="h-7 w-full" data-testid={`tracks-preview-${c.id}-${t.index}`} />}
      </div>
    ))}
  </div>
);

export const CameraTracks = ({ projectId }) => {
  const { data, setData, refresh } = usePoll(() => api.audioTracks(projectId), 6000, [projectId]);
  const [clip, setClip] = useState({ filename: "", track: "" });
  if (!data) return null;
  const cams = Object.values(data.cameras || {});
  const running = data.job && ["queued", "running"].includes(data.job.status);
  const pick = async (cid, track) => { try { setData(await api.setCameraTrack(projectId, cid, track)); toast.success("Track set — proxies are re-cut with it"); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const clipSet = async () => {
    try { const r = await api.setClipTrack(projectId, clip.filename, clip.track === "" ? null : +clip.track); toast.success(`Clip override saved (${r.recut} re-cut)`); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="studio-card p-5 space-y-4" data-testid="camera-tracks">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2"><AudioLines size={16} className="text-[#00F0FF]" /><span className="font-head font-bold">Camera Audio Tracks</span></div>
        <button className="btn btn-gold !py-1.5 text-xs" disabled={running} onClick={async () => { await api.analyzeTracks(projectId); toast.success("Analysing tracks"); refresh(); }} data-testid="btn-analyze-tracks">
          {running ? <Loader2 size={13} className="animate-spin" /> : <ScanLine size={13} />}{running ? `Analysing ${data.job.progress || 0}%` : "Analyse tracks"}</button>
      </div>
      <p className="text-xs text-[#64748B]" data-testid="tracks-summary">{data.multi_track_clips} multi-track clip(s). The cleanest track (loudest speech, lowest noise) is picked per camera; it's used for proxies, transcription and render.{data.recut_pending ? ` Re-cutting ${data.recut_pending} proxy(ies)…` : ""}</p>
      {cams.length === 0 ? <div className="text-xs text-[#64748B]" data-testid="tracks-empty">No multi-track cameras analysed yet.</div> : cams.map((c) => <Camera key={c.id} c={c} onPick={pick} />)}
      {cams.length > 0 && (
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[200px]"><label className="metric-label block mb-1">Per-clip override</label>
            <input className="studio-input !py-1.5 text-sm" placeholder="Clip0006.MXF" value={clip.filename} onChange={(e) => setClip({ ...clip, filename: e.target.value })} data-testid="tracks-clip-name" /></div>
          <select className="studio-input !py-1.5 text-sm w-36" value={clip.track} onChange={(e) => setClip({ ...clip, track: e.target.value })} data-testid="tracks-clip-track">
            <option value="">Camera default</option>{[0, 1, 2, 3].map((i) => <option key={i} value={i}>{`Track ${i + 1}`}</option>)}
          </select>
          <button className="btn btn-ghost !py-1.5 text-sm" disabled={!clip.filename.trim()} onClick={clipSet} data-testid="tracks-clip-apply"><Wand2 size={13} />Apply</button>
        </div>
      )}
      {data.clip_overrides.length > 0 && <p className="text-xs text-[#94A3B8]" data-testid="tracks-overrides">Overrides: {data.clip_overrides.map((o) => `${o.filename} → Track ${o.audio_track + 1}`).join(" · ")}</p>}
    </div>
  );
};
